/**
 * O básico do CPTu: qt, Rf, γ, σv, u₀, σ'v, Qt1, Fr, Bq, n, Cn, Qtn, Ic, Ic SBT,
 * U₂ e I(B) — as colunas da aba «Basic» do CPeT-IT (spec 07 §5.2, item 2;
 * RN-252).
 *
 * As fórmulas são as da lista do CPeT-IT (a última folha do relatório 2609350)
 * e das referências dela: γ de Robertson & Cabal (2010); Qt1, Fr, Bq e a
 * normalização com n variável de Robertson (2009); Ic SBT de Robertson (2010);
 * I(B) de Robertson (2016). Unidades do motor (`unidades.ts`): qt em MPa, as
 * tensões e as poropressões em kPa, γ em kN/m³.
 *
 *   qt  = qc + u₂·(1 − a)/1000                    Rf  = 100·fs/qt
 *   γ   = γw·(0,27·log Rf + 0,36·log(qt/pa) + 1,236)
 *   σv  = Σ γ·Δz                                   u₀  = γw·(z − NA), ≥ 0
 *   σ'v = σv − u₀                                  Qt1 = (qt − σv)/σ'v
 *   Fr  = 100·fs/(qt − σv)                         Bq  = (u₂ − u₀)/(qt − σv)
 *   Cn  = (pa/σ'v)ⁿ   Qtn = ((qt − σv)/pa)·Cn   Ic = √((3,47 − log Qtn)² + (log Fr + 1,22)²)
 *   n   = 0,381·Ic + 0,05·(σ'v/pa) − 0,15, n ≤ 1
 *   Ic SBT = √((3,47 − log(qt/pa))² + (log Rf + 1,22)²)
 *   U₂  = (u₂ − u₀)/σ'v                            I(B) = 100·(Qtn + 10)/(70 + Qtn·Fr)
 *
 * O que a fórmula publicada não fixa saiu do gabarito 2609350 (as dez
 * verticais, 18.711 leituras, CPeT-IT v3.9.7.4) e está anotado onde é feito,
 * com a evidência: a fração das 18.711 linhas dentro da tolerância do §5.3 com
 * a escolha e com a alternativa (tarefa 311.3, remedida na revisão final da
 * onda 311). O que vem só do dado do CPeT-IT, sem fonte publicada, vai marcado
 * «COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA», com a regra que o RT
 * assina entre colchetes — uma das `REGRAS_A_ASSINAR_BASICO` —, como em
 * `classificacao.ts`, `correlacoes.ts` e `dissipacao.ts`. Resumo:
 *
 *  1. As contas usam a MÉDIA MÓVEL de 3 leituras de qc, fs e u₂ (premissa
 *     `mediaMovel`), centrada, com a ponta repetindo a leitura da ponta; as
 *     colunas qc, fs e u₂ do CPeT-IT são as leituras cruas — COMPORTAMENTO
 *     COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR PELO RT
 *     [bas.media-movel-nas-contas]. Contraria o texto do §5.1 da spec («só nos
 *     gráficos e no PDF»); quem decidiu foi o gabarito. Sem a média, o qt bate
 *     em 52,37 % das linhas, o Rf em 28,75 % e o Qtn em 32,46 %; com ela,
 *     99,99 %, 100 % e 99,99 %. Só qc na média (fs e u₂ crus) põe o Rf em
 *     37,96 %; só o u₂ cru põe o U₂ em 37,26 %. Com a janela encolhendo na ponta
 *     (em vez de repetir a leitura), o Qt1 cai para 99,03 % e o U₂ para 99,24 %.
 *     O `.cpt` tem `QcFilter = FsFilter = U2Filter = 0`: não é filtro do usuário.
 *  2. pa = 101,325 kPa em TODAS as contas do básico que usam pa —
 *     COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR PELO RT
 *     [bas.pa-101325]: γ (a tolerância do §5.3 não separa 100 de 101,325 no γ,
 *     mas arredondado a duas casas ele é o do gabarito em 99,995 % das linhas,
 *     contra 6,61 % com 100), Ic SBT (99,995 % contra 97,87 % com 100) e
 *     n/Cn/Qtn (Cn 99,995 % contra 3,46 % com 100).
 *  3. γ com piso: γ/γw ≥ 1,4 (13,734 kN/m³ com γw = 9,81) — COMPORTAMENTO
 *     COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR PELO RT
 *     [bas.gama-piso]; o `.cpt` só diz que o limite está ligado
 *     (`LimitUnitWeight`), não o valor. São 1.264 leituras em 13,73 no
 *     gabarito, e o Cn da primeira leitura do CPTu01, 737,77, só fecha com
 *     σv = 13,734 × 0,01 (13,73 daria 737,98). Rf = 0 (fs = 0) dá log −∞ e cai
 *     no piso. Sem o piso, γ 93,98 % e σv 58,22 % (CPTu07, 0,05 %). Teto não
 *     há: o maior γ do gabarito (20,75, a última leitura do CPTu05) é valor da
 *     fórmula, não corte (os seguintes são 20,65, 20,62, 20,61…), e nenhuma
 *     fonte dá o teto — não se inventa.
 *  4. γ que a fórmula não dá (qt ≤ 0, Rf < 0, leitura que falta; sem limite,
 *     também Rf = 0) é o γ fixo (19, o `DefaultGamma` do `.cpt`), mesmo com o
 *     γ automático — COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A
 *     ASSINAR PELO RT [bas.gama-reserva]: é o 19 do gabarito no CPTu02 de 0,92
 *     a 0,94 m, no CPTu03, CPTu04 e CPTu07 a 0,01 m e no CPTu10 a 0,85 e
 *     0,86 m. Com o piso no lugar do fixo, σv 98,02 % (CPTu02, 93,04 %).
 *  5. σv soma o γ da PRÓPRIA leitura vezes o passo: 100 % das linhas, contra
 *     99,51 % com a média (γᵢ₋₁ + γᵢ)/2 e 98,72 % com γᵢ₋₁. A primeira leitura
 *     leva o peso da coluna acima, σv₁ = γ₁·z₁ — COMPORTAMENTO COPIADO DO
 *     CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR PELO RT [bas.sv-primeira-leitura]
 *     (o CPTu05, que começa a 2,27 m depois do pré-furo, tem σv = 14,61 × 2,27
 *     = 33,16 kPa; com σv₁ = 0, σv 82,96 %).
 *  6. O n parte de 1 e itera; para quando |Δn| < 0,01 — e fica com o n NOVO,
 *     recalculando Cn, Qtn e Ic com ele — ou na 51ª volta — COMPORTAMENTO
 *     COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR PELO RT
 *     [bas.n-iteracao]. Assim o n bate em 99,995 % das linhas; ficar com o n de
 *     antes da última volta dá 77,43 %, iterar até convergir de verdade dá
 *     96,88 %, e parar na 50ª ou na 52ª volta dá 99,96 % (perto da superfície o
 *     n oscila sem convergir, e o CPeT-IT publica o da 51ª volta: CPTu02,
 *     0,01 m, n = 0,41).
 *  7. Sem Ic (Fr ≤ 0, Qtn ≤ 0, leitura que falta), o n fica no 1 de partida e
 *     Cn = pa/σ'v continua valendo — COMPORTAMENTO COPIADO DO CPeT-IT, SEM
 *     FONTE PUBLICADA — A ASSINAR PELO RT [bas.n-sem-ic]: o CPTu01 de 0,01 a
 *     0,07 m (fs = 0) tem n = 1 e Cn = 737,77 no gabarito (com n e Cn sem valor
 *     ali, 99,82 %). Sem σ'v > 0 a normalização não existe: n, Cn, Qtn e Ic sem
 *     valor — e Qt1 e U₂, que dividem por σ'v, também não (um σ'v negativo
 *     daria um número de sinal trocado, não uma conta; o gabarito não tem esse
 *     caso).
 *
 * Três divergências conhecidas, que ficam (nenhuma passa de 0,02 % das linhas
 * de uma coluna): o CPeT-IT conta a leitura que falta (o −9999 do equipamento,
 * NaN aqui) como ZERO na média dos vizinhos — o motor não a conta (CPTu03 e
 * CPTu04 a 0,02 m); publica Ic = 4,06 onde a fórmula dá 5,12 (CPTu02, 0,95 m)
 * e 4,12 (CPTu10, 0,87 m) — um teto do Ic: a coluna Ic do motor não o tem (as
 * 2 linhas ficam fora), e o Kc o aplica (comportamento copiado do CPeT-IT: o Kc
 * de Robertson 2022, Can. Geotech. J., doi:10.1139/cgj-2020-0657, eq. 4a, vale
 * até Ic 3,0 no artigo; `correlacoes.ts`, escolha 13); e
 * calcula o I(B) da leitura que falta com o Qtn = −1 que usa como «não se
 * aplica» (12,86 no CPTu03 e no CPTu04 a 0,01 m) — aqui é NaN.
 *
 * Fora isso, a conta é a fórmula: o que dá número (inclusive negativo, como o
 * Fr e o Qt1 onde qt < σv, que o CPeT-IT também publica) fica; o que não dá —
 * log de valor ≤ 0, divisão por zero — é NaN, nunca Infinity, e nada lança.
 */
import type { Leituras, Premissas, RegraAAssinar, Serie } from './tipos';
import { PA_KPA } from './unidades';

/** O básico de uma vertical: uma série por grandeza, uma posição por leitura. */
export interface Basico {
  /** Quantas leituras entraram. */
  total: number;
  /** Profundidade, m — como entrou. */
  z: Serie;
  /** qc, MPa — a leitura como entrou (as contas usam a média móvel, ver o cabeçalho). */
  qc: Serie;
  /** fs, kPa — a leitura como entrou. */
  fs: Serie;
  /** u₂, kPa — a leitura como entrou. */
  u2: Serie;
  /** Resistência de ponta corrigida, MPa. */
  qt: Serie;
  /** Razão de atrito, %. */
  rf: Serie;
  /** Peso específico, kN/m³. */
  gama: Serie;
  /** Tensão vertical total, kPa. */
  sv: Serie;
  /** Poropressão de equilíbrio, kPa. */
  u0: Serie;
  /** Tensão vertical efetiva, kPa. */
  svl: Serie;
  /** Resistência normalizada com n = 1. */
  qt1: Serie;
  /** Razão de atrito normalizada, %. */
  fr: Serie;
  /** Razão de poropressão. */
  bq: Serie;
  /** O expoente da normalização (iterado). */
  n: Serie;
  /** Fator de normalização, (pa/σ'v)ⁿ. */
  cn: Serie;
  /** Índice de comportamento normalizado. */
  ic: Serie;
  /** Resistência normalizada com n variável. */
  qtn: Serie;
  /** Índice de comportamento não normalizado. */
  icSbt: Serie;
  /** Excesso de poropressão normalizado, (u₂ − u₀)/σ'v. */
  U2: Serie;
  /** Índice I(B) de Robertson (2016). */
  ib: Serie;
}

/**
 * γ/γw mínimo do γ automático com limite — COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA —
 * A ASSINAR PELO RT [bas.gama-piso] (escolha 3 do cabeçalho).
 */
const GAMA_RAZAO_MINIMA = 1.4;

/**
 * O n de partida da iteração — e o que fica quando o Ic não existe (escolhas 6 e 7; o n = 1 sem Ic:
 * COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR PELO RT [bas.n-sem-ic]).
 */
const N_INICIAL = 1;

/** A iteração do n para quando ele muda menos que isto de uma volta para a outra… */
const N_TOLERANCIA = 0.01;

/**
 * …ou nesta volta, a que o CPeT-IT publica quando o n oscila sem convergir — COMPORTAMENTO COPIADO
 * DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR PELO RT [bas.n-iteracao] (escolha 6).
 */
const N_VOLTAS_MAXIMAS = 51;

/**
 * Os comportamentos do básico sem fonte publicada, copiados do CPeT-IT (escolhas 1 a 7 do
 * cabeçalho), um por rótulo do comentário, com o mesmo id entre colchetes: a conformidade
 * (`conformidade.ts`) os leva ao RT para assinar, e o `298.64` confere que a lista e os rótulos são
 * os mesmos e que as frações «com a regra» são as da conformidade de agora.
 */
export const REGRAS_A_ASSINAR_BASICO: readonly RegraAAssinar[] = Object.freeze(
  [
    {
      id: 'bas.media-movel-nas-contas',
      arquivo: 'basico.ts',
      descricao: 'As contas do básico — e, por ele, a classificação e as correlações — usam a média móvel de 3 '
        + 'leituras de qc, fs e u₂ (a premissa mediaMovel), centrada, com a ponta repetindo a leitura da ponta; as '
        + 'colunas qc, fs e u₂ continuam as leituras cruas. Contraria o texto do §5.1 da spec («só nos gráficos e no '
        + 'PDF»): quem decidiu foi o gabarito.',
      evidencia: 'Com a média, qt 99,99 %, Rf 100,00 % e Qtn 99,99 % das 18.711 linhas; sem ela (mediaMovel = 1), '
        + '52,37 %, 28,75 % e 32,46 %. Só o qc na média (fs e u₂ crus): Rf 37,96 %; só o u₂ cru: U₂ 37,26 %; com a '
        + 'janela encolhendo na ponta, Qt1 99,03 % e U₂ 99,24 %. O .cpt tem QcFilter = FsFilter = U2Filter = 0: não '
        + 'é filtro do usuário.',
    },
    {
      id: 'bas.pa-101325',
      arquivo: 'basico.ts',
      descricao: 'pa = 101,325 kPa (a atmosfera padrão, não os 100 kPa redondos) nas três contas do básico que usam '
        + 'pa: o γ de Robertson & Cabal (2010), o Ic SBT e a normalização (n, Cn e Qtn).',
      evidencia: 'γ: a tolerância do §5.3 não separa (100 % com os dois), mas o γ arredondado a duas casas é o do '
        + 'gabarito em 99,995 % das linhas com 101,325 e em 6,61 % com 100. Ic SBT 99,995 % das 18.711 linhas, '
        + 'contra 97,87 % com 100; Cn 99,995 %, contra 3,46 % com 100 (e Qtn 87,91 %).',
    },
    {
      id: 'bas.gama-piso',
      arquivo: 'basico.ts',
      descricao: 'γ automático (Robertson & Cabal 2010) com piso γ ≥ 1,4·γw (13,734 kN/m³ com γw = 9,81), ligado '
        + 'pela premissa gamaLimitar (o LimitUnitWeight do .cpt, que não guarda o valor); Rf = 0 (log −∞) cai no '
        + 'piso. Sem teto: nenhuma fonte o dá, e o maior γ do gabarito (20,75) é valor da fórmula.',
      evidencia: '1.264 leituras em 13,73 no gabarito; o Cn da primeira leitura do CPTu01 (737,77) só fecha com '
        + 'σv = 13,734 × 0,01 (13,73 daria 737,98). Com o piso, γ 100,00 % e σv 100,00 % das 18.711 linhas; sem ele '
        + '(gamaLimitar = false), γ 93,98 % e σv 58,22 % (CPTu07, 0,05 %).',
    },
    {
      id: 'bas.gama-reserva',
      arquivo: 'basico.ts',
      descricao: 'Onde a fórmula do γ não dá número (qt ≤ 0, Rf < 0, leitura que falta; sem o piso, também Rf = 0), '
        + 'vale o γ fixo (gamaFixo, o DefaultGamma = 19 do .cpt), mesmo com o γ automático.',
      evidencia: 'É o 19 do gabarito no CPTu02 de 0,92 a 0,94 m, no CPTu03, CPTu04 e CPTu07 a 0,01 m e no CPTu10 a '
        + "0,85 e 0,86 m. Com o γ fixo, σv 100,00 % das 18.711 linhas; com o piso (13,734) no lugar, σv 98,02 % "
        + "(CPTu02, 93,04 %) e σ'v 93,67 %; sem γ nenhum (NaN), σv 51,04 %.",
    },
    {
      id: 'bas.sv-primeira-leitura',
      arquivo: 'basico.ts',
      descricao: 'A primeira leitura leva o peso da coluna acima dela: σv₁ = γ₁·z₁, com o γ da própria primeira '
        + 'leitura — também quando a vertical começa funda, depois do pré-furo.',
      evidencia: 'O CPTu05, que começa a 2,27 m, tem σv = 14,61 × 2,27 = 33,16 kPa no gabarito. Com a regra, σv '
        + "100,00 % das 18.711 linhas; com σv₁ = 0, 82,96 % (CPTu05, 0 %) e σ'v 67,48 %.",
    },
    {
      id: 'bas.n-iteracao',
      arquivo: 'basico.ts',
      descricao: 'O n de Robertson (2009) parte de 1 e itera; para quando |Δn| < 0,01 — ficando com o n NOVO, com '
        + 'Cn, Qtn e Ic recalculados com ele — ou na 51ª volta, a que o CPeT-IT publica quando o n oscila sem '
        + 'convergir perto da superfície.',
      evidencia: 'n 99,995 % das 18.711 linhas; ficar com o n de antes da última volta, 77,43 %; iterar até convergir '
        + 'de verdade, 96,88 %; parar na 50ª ou na 52ª volta, 99,96 % (no CPTu02 a 0,01 m o n cicla entre 0,41 e 1, '
        + 'e o gabarito publica o da 51ª volta, 0,41).',
    },
    {
      id: 'bas.n-sem-ic',
      arquivo: 'basico.ts',
      descricao: "Sem Ic (Fr ≤ 0, Qtn ≤ 0, leitura que falta), o n fica no 1 de partida e Cn = pa/σ'v continua "
        + "valendo (com σ'v > 0); sem σ'v > 0, n, Cn, Qtn e Ic ficam sem valor (o gabarito não tem esse caso).",
      evidencia: 'O CPTu01 de 0,01 a 0,07 m (fs = 0) tem n = 1 e Cn = 737,77 no gabarito. Com a regra, n e Cn '
        + '99,995 % das 18.711 linhas; com n e Cn sem valor onde não há Ic, 99,82 % (CPTu10, 99,57 %).',
    },
  ].map((r) => Object.freeze(r)),
);

/**
 * O número, ou NaN quando ele é infinito ou não existe; o −0 da aritmética (0 sobre um negativo)
 * sai 0, para não aparecer «−0,00» numa tabela.
 */
const finito = (x: number): number => (Number.isFinite(x) ? (x === 0 ? 0 : x) : NaN);

/**
 * DIAGNÓSTICO — NÃO USAR NO FLUXO OFICIAL: exportada para os testes e as
 * ferramentas compararem os canais suavizados; quem calcula é `calcularBasico`
 * (e `processarVertical`), que já a aplica.
 *
 * A média móvel que o básico aplica a qc, fs e u₂ antes das contas, como o
 * CPeT-IT — COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR
 * PELO RT [bas.media-movel-nas-contas] (escolha 1 do cabeçalho): centrada em
 * `janela` leituras (uma janela par vale a ímpar seguinte), com a ponta da
 * série REPETINDO a leitura da ponta — a primeira leitura de uma janela de 3 é
 * (2·s₀ + s₁)/3. É diferente da
 * `mediaMovel` da edição, que encolhe a janela na ponta: esta reproduz o
 * gabarito, aquela é o filtro que o engenheiro escolhe.
 *
 * A leitura que falta (NaN) não entra na média dos vizinhos e continua NaN.
 * Janela 1 (ou menor, ou não numérica) devolve uma cópia sem média; a série de
 * entrada nunca muda. As posições da janela além da ponta não são percorridas
 * uma a uma: conta-se quantas são, e uma janela maior que a série (10¹⁰
 * digitado por engano) custa o mesmo que a série.
 */
export function mediaMovelDoBasico(s: Serie, janela: number): Serie {
  const n = s.length;
  const saida = new Float64Array(n);
  const meia = Number.isFinite(janela) && janela > 1 ? Math.floor(janela / 2) : 0;
  const primeira = n > 0 ? s[0] : NaN;
  const ultima = n > 0 ? s[n - 1] : NaN;
  for (let i = 0; i < n; i++) {
    if (meia === 0 || Number.isNaN(s[i])) {
      saida[i] = s[i];
      continue;
    }
    let soma = 0;
    let validos = 0;
    // Antes do começo, a primeira leitura repetida (meia − i vezes); na mesma ordem da soma posição
    // a posição, para a janela de 3 dar os mesmos bits.
    const antes = meia - i;
    if (antes > 0 && !Number.isNaN(primeira)) {
      soma += antes * primeira;
      validos += antes;
    }
    const fim = Math.min(n - 1, i + meia);
    for (let j = Math.max(0, i - meia); j <= fim; j++) {
      const v = s[j];
      if (Number.isNaN(v)) continue;
      soma += v;
      validos++;
    }
    // Depois do fim, a última leitura repetida.
    const depois = i + meia - (n - 1);
    if (depois > 0 && !Number.isNaN(ultima)) {
      soma += depois * ultima;
      validos += depois;
    }
    saida[i] = soma / validos; // o centro é válido: validos ≥ 1
  }
  return saida;
}

/**
 * γ de uma leitura (Robertson & Cabal 2010, com pa = 101,325 kPa — COMPORTAMENTO
 * COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR PELO RT [bas.pa-101325]),
 * ou o fixo. Com limite, γ ≥ 1,4·γw (o log −∞ de Rf = 0 cai no limite); o que
 * continua sem número vira o γ fixo — COMPORTAMENTO COPIADO DO CPeT-IT, SEM
 * FONTE PUBLICADA — A ASSINAR PELO RT [bas.gama-reserva] (escolhas 2 a 4 do
 * cabeçalho).
 */
function gamaDaLeitura(rf: number, qtKpa: number, p: Premissas): number {
  if (p.gamaModo === 'fixo') return p.gamaFixo;
  let gama = p.gamaW * (0.27 * Math.log10(rf) + 0.36 * Math.log10(qtKpa / PA_KPA) + 1.236);
  if (p.gamaLimitar) gama = Math.max(gama, GAMA_RAZAO_MINIMA * p.gamaW); // Math.max(NaN, x) é NaN
  return Number.isFinite(gama) ? gama : p.gamaFixo;
}

/**
 * O perfil de u₀ (kPa) em função de z: hidrostático a partir do NA (zero acima
 * dele), ou pelos pontos do engenheiro — interpolação linear em z, constante
 * fora do primeiro e do último ponto, em qualquer ordem que venham. Sem ponto
 * válido, o perfil não existe e u₀ é NaN.
 */
function perfilDeU0(p: Premissas): (z: number) => number {
  if (p.u0Modo !== 'pontos') return (z) => p.gamaW * Math.max(0, z - p.na);
  const pontos = (Array.isArray(p.u0Pontos) ? p.u0Pontos : [])
    .filter((q) => q && Number.isFinite(q.z) && Number.isFinite(q.u0))
    .map((q) => ({ z: q.z, u0: q.u0 }))
    .sort((a, b) => a.z - b.z);
  if (pontos.length === 0) return () => NaN;
  const primeiro = pontos[0];
  const ultimo = pontos[pontos.length - 1];
  return (z) => {
    if (Number.isNaN(z)) return NaN;
    if (z <= primeiro.z) return primeiro.u0;
    if (z >= ultimo.z) return ultimo.u0;
    let k = 0;
    while (pontos[k + 1].z <= z) k++; // pontos[k].z ≤ z < pontos[k + 1].z: o divisor é positivo
    const a = pontos[k];
    const b = pontos[k + 1];
    return a.u0 + ((b.u0 - a.u0) * (z - a.z)) / (b.z - a.z);
  };
}

/** Cn, Qtn e Ic para um n, e o n a que eles levam. */
interface Normalizacao {
  n: number;
  cn: number;
  qtn: number;
  ic: number;
}

const icDe = (qtn: number, fr: number): number =>
  finito(Math.sqrt((3.47 - Math.log10(qtn)) ** 2 + (Math.log10(fr) + 1.22) ** 2));

/**
 * A normalização de Robertson (2009) com n variável, iterada como o CPeT-IT
 * (escolhas 6 e 7 do cabeçalho: COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE
 * PUBLICADA — A ASSINAR PELO RT [bas.n-iteracao] e [bas.n-sem-ic]), com
 * pa = 101,325 kPa ([bas.pa-101325]). `qn` = qt − σv e `svl` = σ'v, em kPa; `fr`
 * em %; `corte` = o corte de Cn (null = sem corte).
 */
function normalizar(qn: number, svl: number, fr: number, corte: number | null): Normalizacao {
  if (!(svl > 0)) return { n: NaN, cn: NaN, qtn: NaN, ic: NaN };
  const cnDe = (n: number): number => {
    const cn = (PA_KPA / svl) ** n;
    return corte === null ? cn : Math.min(cn, corte);
  };
  let n = N_INICIAL;
  let cn = cnDe(n);
  let qtn = (qn / PA_KPA) * cn;
  let ic = icDe(qtn, fr);
  for (let volta = 1; ; volta++) {
    let novo = 0.381 * ic + 0.05 * (svl / PA_KPA) - 0.15;
    if (novo > 1) novo = 1;
    if (Number.isNaN(novo)) break; // sem Ic, o n fica onde está (o 1 de partida)
    const parou = Math.abs(novo - n) < N_TOLERANCIA || volta >= N_VOLTAS_MAXIMAS;
    n = novo; // fica com o n novo, e Cn, Qtn e Ic são os dele
    cn = cnDe(n);
    qtn = (qn / PA_KPA) * cn;
    ic = icDe(qtn, fr);
    if (parou) break;
  }
  return { n, cn: finito(cn), qtn: finito(qtn), ic };
}

/**
 * O básico de uma vertical, leitura a leitura, com as premissas dadas. As
 * leituras são as de trabalho (já editadas, `aplicarEdicao`), com a
 * profundidade crescente; a que falta é NaN. Devolve séries novas — a entrada
 * nunca muda — e nunca lança: o que não existe é NaN.
 */
export function calcularBasico(l: Leituras, p: Premissas): Basico {
  const total = l.profundidade.length;
  const nova = (): Serie => new Float64Array(total);
  const b: Basico = {
    total,
    z: l.profundidade.slice(), qc: l.qc.slice(), fs: l.fs.slice(), u2: l.u2.slice(),
    qt: nova(), rf: nova(), gama: nova(), sv: nova(), u0: nova(), svl: nova(), qt1: nova(), fr: nova(),
    bq: nova(), n: nova(), cn: nova(), ic: nova(), qtn: nova(), icSbt: nova(), U2: nova(), ib: nova(),
  };

  // As contas correm sobre a média móvel dos três canais (escolha 1).
  const qc = mediaMovelDoBasico(l.qc, p.mediaMovel);
  const fs = mediaMovelDoBasico(l.fs, p.mediaMovel);
  const u2 = mediaMovelDoBasico(l.u2, p.mediaMovel);
  const u0De = perfilDeU0(p);
  const corte = typeof p.cnCorte === 'number' && p.cnCorte > 0 ? p.cnCorte : null;

  for (let i = 0; i < total; i++) {
    const z = l.profundidade[i];
    const qt = qc[i] + (u2[i] * (1 - p.a)) / 1000; // MPa
    const qtKpa = 1000 * qt;
    const rf = (100 * fs[i]) / qtKpa;
    const gama = gamaDaLeitura(rf, qtKpa, p);
    // σv₁ = γ₁·z₁ (a coluna acima da primeira leitura: COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE
    // PUBLICADA — A ASSINAR PELO RT [bas.sv-primeira-leitura]); depois, o γ da própria leitura × Δz
    // (escolha 5).
    const sv = i === 0 ? gama * z : b.sv[i - 1] + gama * (z - l.profundidade[i - 1]);
    const u0 = u0De(z);
    const svl = sv - u0;
    const qn = qtKpa - sv; // a resistência líquida, kPa
    const fr = finito((100 * fs[i]) / qn);
    const norm = normalizar(qn, svl, fr, corte);

    b.qt[i] = finito(qt);
    b.rf[i] = finito(rf);
    b.gama[i] = gama;
    b.sv[i] = finito(sv);
    b.u0[i] = finito(u0);
    b.svl[i] = finito(svl);
    b.qt1[i] = svl > 0 ? finito(qn / svl) : NaN; // sem σ'v positivo, como a normalização
    b.fr[i] = fr;
    b.bq[i] = finito((u2[i] - u0) / qn);
    b.n[i] = norm.n;
    b.cn[i] = norm.cn;
    b.qtn[i] = norm.qtn;
    b.ic[i] = norm.ic;
    b.icSbt[i] = finito(Math.sqrt((3.47 - Math.log10(qtKpa / PA_KPA)) ** 2 + (Math.log10(rf) + 1.22) ** 2));
    b.U2[i] = svl > 0 ? finito((u2[i] - u0) / svl) : NaN;
    b.ib[i] = finito((100 * (norm.qtn + 10)) / (70 + norm.qtn * fr));
  }
  return b;
}
